/**
 * Make an image file safe for social-share previews before upload.
 *
 * Facebook rejects AVIF for og:image (and falls back to the site logo);
 * LinkedIn is unreliable with WebP. Editors mostly hit this by saving images
 * from other news sites, which serve AVIF. The picker already produces JPEG
 * when the editor crops; this covers the direct-upload path. The browser does
 * the decoding (Chrome handles every AVIF profile), so no server codec is needed.
 */
const SAFE = /^image\/(jpeg|png|gif)$/i;
const SAFE_EXT = /\.(jpe?g|png|gif)$/i;

export async function ensureShareableImage(file: File): Promise<File> {
  if (SAFE.test(file.type) || (!file.type && SAFE_EXT.test(file.name))) return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) {
    throw new Error("This image format is not supported. Please upload a JPG or PNG.");
  }
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0);
  bitmap.close?.();
  const blob: Blob | null = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
  if (!blob) throw new Error("Could not convert this image. Please upload a JPG or PNG.");
  return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
}
